import * as React from 'react';
import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Checkbox from 'antd/lib/checkbox';
import { useCtx } from 'one-ui-provider';
import classNames from 'classnames';
import L from 'leaflet';
import { Map, TileLayer } from 'react-leaflet';
import { OpenStreetMapProvider, GeoSearchControl } 'leaflet-geosearch';
import { CalledDest } from 'one-state-calldest/src/CalledDest';
import { PharmacyData } from 'one-state-patient-pharmacy/src/PharmacyData';
import pharIcon from 'one-ui-svg/images/pharmacy.png';
import mapIcon from 'one-ui-svg/images/redmarker.png';
import hosIcon from 'one-ui-svg/images/doctor.png';
import 'leaflet-geosearch/dist/geosearch.css';
import { Image_URL } from 'one-ui-image';

interface MapSettings {
  pharmCheck: boolean;
  prefPharCheck: boolean;
  hospCheck: boolean;
  callDestCheck: boolean;
}
interface Props {
  address?: string;
  calldest?: CalledDest;
  pharmacies?: PharmacyData[];
  expanded?: boolean;
  settings?: MapSettings;
  setMapSettings: any;
}

/**
 * This card uses Leaflet library to display map and use argcis-esri-leaflet-geocoder for the nearset pharmacy,hospital address
 * @param prop:address
 * @param prop:calldest // calling destination address
 */
export const LeafletMap = forwardRef(({
  address,
  calldest,
  pharmacies,
  expanded,
  settings,
  setMapSettings,
}: Props, ref) => {
  const { log, settings: { map_config, compatible }, resolver, static_resource_url } = useCtx();
  const { contact_center_type } = compatible;
  const { arcgis: { addressargcis,pharmacyarcgis, hospitalarcgis }, mapui: { attribution, attributionurl } } = map_config;
  const mainRef = useRef<HTMLDivElement>(null);
  const leafletMap: any = useRef(null);
  const [mapAddress, setMapAddress] = useState('');
  const [mapaddr, setMapAddr] = useState({});
  const [pharaddr, setPharAddr] = useState([]);
  
  const [pharmCheck, setPharmCheck] = useState(settings.pharmCheck ? true : false);
  const [prefPharMapAddr, setPrefPharMapAddr] = useState([]);
  const [prefPharCheck, setPrefPharCheck] = useState(settings.prefPharCheck ? true : false);
  const [prefPharAddr, setPrefPharAddr] = useState([]);
  const [hospaddr, setHospAddr] = useState([]);
  const [hospCheck, setHospCheck] = useState(settings.hospCheck ? true : false);
  const [callDestCheck, setCallDestCheck] = useState(settings.callDestCheck ? true : false);

  if (settings.pharmCheck !== pharmCheck) setPharmCheck(settings.pharmCheck)
  if (settings.prefPharCheck !== prefPharCheck) setPrefPharCheck(settings.prefPharCheck)
  if (settings.hospCheck !== hospCheck) setHospCheck(settings.hospCheck)
  if (settings.callDestCheck !== callDestCheck) setCallDestCheck(settings.callDestCheck)
  
  // Default Center to show in map on Load/ Reset
  const [center, setCenter] = useState<L.LatLngExpression>([37.7833, -122.4167])
  /* Marker Img urls */
  const hosMarker = L.icon({
    iconUrl: Image_URL(resolver(hosIcon), `${static_resource_url}doctor.ac3eba49.png`),
    iconSize: [20, 20]
  });
  const phmyMarker = L.icon({
    iconUrl: Image_URL(resolver(pharIcon), `${static_resource_url}pharmacy.d89a2861.png`),
    iconSize: [20, 20]
  });
  const markerIcon = L.icon({
    iconUrl: Image_URL(resolver(mapIcon), `${static_resource_url}redmarker.e1e33397.png`),
    iconSize: [25, 25]
  });

  //To Update the address
  useEffect(() => {
    setMapAddress(address);

  },[address]);

  useImperativeHandle(ref, () => ({
    showMarkers() {
      initializeMap();
    },
  }))

  // Preferred pharmacies map addresses
  useEffect(() => {
    getPreferredPharmacyMapAddress();
  }, [pharmacies, addressargcis]);

  const initializeMap = (theMarker?: any) => {
    if (pharmCheck) {
      getMarkers(pharmacyarcgis, phmyMarker, "pharmacy", theMarker);
    } else {
      if (pharaddr != []) {
        removeMarkers(pharaddr);
      }
    }

    if (hospCheck) {
      getMarkers(hospitalarcgis, hosMarker, "hospital", theMarker);
    } else {
      if (hospaddr != []) {
        removeMarkers(hospaddr);
      }
    }
  }

  /**
   * This method is invoked on address change(patient selection)
   * clear exisitng markers
   * reset  pharmacy or hospital checkboxes
   * map the location of patient address
   */
  useEffect(() => {
    const map = leafletMap.current.leafletElement;
    //clear values beforre laoding new adress
    if (mapaddr != {}) {
      map.removeLayer(mapaddr);
      setMapAddr({});
    }

    const searchControl = new GeoSearchControl({
      provider: new OpenStreetMapProvider(),
      retainZoomLevel: true,
    });
    if(searchControl?._map?.DomEvent) {
      map.addControl(searchControl);
    }
    map.on('geosearch/showlocation', handleAddressSelect);

    let theMarker = L.marker();
    // "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?outSr=4326&forStorage=false&outFields=*&maxLocations=20&singleLine="+mapAddress+"&f=json"
    var mapaddressurl = addressargcis + mapAddress + '&f=json';
    const RetriveAddress = fetch(mapaddressurl);
    RetriveAddress.then((response) => {
      return response.json();
    })
      .then((result) => {
        var candidates = result?.candidates;
        if (candidates && candidates[0]?.location) {
          const mapCoordinates = candidates[0].location;
          // long,latitude returned
          let { x, y } = mapCoordinates;
          // center = L.LatLngExpression = [y, x];
          setCenter([y, x]);
          theMarker = L.marker([y, x], { icon: markerIcon }).addTo(map);

          let markerPopup = mapAddress;
          if (callDestCheck) {
            if (calldest) {
              if (calldest.hoursoperation) {
                  const daysdata: any = calldest.hoursoperation;
                  let hoursop;
                  if ((daysdata.monday_start === daysdata.tuesday_start) &&
                      (daysdata.monday_start === daysdata.wednesday_start) &&
                      (daysdata.monday_start === daysdata.thursday_start) &&
                      (daysdata.monday_start === daysdata.friday_start)) {
                      hoursop = ` Mon-Fri: ${daysdata.monday_start ? daysdata.monday_start : ""} - ${daysdata.monday_end ? daysdata.monday_end : ""} | Sat: ${daysdata.saturday_start ? daysdata.saturday_start : ""}  - ${daysdata.saturday_end ? daysdata.saturday_end : ""} | Sun: ${daysdata.sunday_start ? daysdata.sunday_start : ""} - ${daysdata.sunday_end ? daysdata.sunday_end : ""} `;
                  }
                  else {
                      hoursop = ` Mon: ${daysdata.monday_start ? daysdata.monday_start : ""} - ${daysdata.monday_end ? daysdata.monday_end : ""} | Tue: ${daysdata.tuesday_start ? daysdata.tuesday_start : ""} - ${daysdata.tuesday_end ? daysdata.tuesday_end : ""} | Wed: ${daysdata.wednesday_start ? daysdata.wednesday_start : ""} - ${daysdata.wednesday_end ? daysdata.wednesday_end : ""}
                      | Thur: ${daysdata.thursday_start ? daysdata.thursday_start : ""} - ${daysdata.thursday_end ? daysdata.thursday_end : ""} | 
                      Fri: ${daysdata.friday_start ? daysdata.friday_start : ""} - ${daysdata.friday_end ? daysdata.friday_end : ""} | Sat: ${daysdata.saturdaty_start ? daysdata.saturday_start : ""} - ${daysdata.saturday_end ? daysdata.saturday_end : ""} | Sun: ${daysdata.sunday_start ? daysdata.sunday_start : ""} - ${daysdata.sunday_end ? daysdata.sunday_end : ""}`
                  }
                  markerPopup += `<br><b>Working hours:</b> ${hoursop}`;
              }
            }
          }
          theMarker.bindPopup(markerPopup).openPopup();
          setMapAddr(theMarker);
          initializeMap(theMarker)
        } else {
          log.debug('[ccw-app],ccw-gadget,leaflet-map', 'geocode for patient address requested', {
            mapAddress,
          });
          log.debug('[ccw-app],ccw-gadget,leaflet-map', 'geocode for patient address results returned empty', {
            result,
          });
        }
      })
      .catch((error) => {
        log.error('[ccw-app],ccw-gadget,leaflet-map', ' error while retrivng geocode for patient address ', {
          error,
        });
      });


    return () => {
      map.off('geosearch/showlocation', handleAddressSelect);
      map.removeControl(searchControl);
    };
  }, [mapAddress]);

  const handleAddressSelect = (addr: any) => {
    const map = leafletMap.current.leafletElement;
    let theMarker = L.marker();
    const mapCoordinates = addr.location;
    let { x, y } = mapCoordinates;
    theMarker = L.marker([y, x], { icon: markerIcon }).addTo(map);
    theMarker.bindPopup(addr.location.label).openPopup();
    setMapAddr(theMarker);
  };

  const getMarkers = (url: string, icon: any, type: any, newMapAddress?: any) => {
    let markersretrun: any = [];
    
    let updatedMapAddress = mapaddr
    if (newMapAddress) {
      updatedMapAddress = newMapAddress
    }
    // check there is map active with customer address
    if (typeof (updatedMapAddress.getLatLng) === 'function') {
      if (updatedMapAddress && updatedMapAddress.getLatLng() && updatedMapAddress.getLatLng()?.lat && updatedMapAddress.getLatLng()?.lng) {
        let lat = updatedMapAddress.getLatLng().lat;
        let lng = updatedMapAddress.getLatLng().lng;
        const urlmodify = url + lng + "," + lat
        log.debug(
          "[ccw-app],ccw-gadget,leaflet-map getMarkers",
          "Argcis request url for nearest co-ordinates ", {
          urlmodify
        });
        const fetchData = () => {
          fetch(urlmodify).then(res => res.json())
            .then(
              (result) => {
                log.debug(
                  "[ccw-app],ccw-gadget,leaflet-map getMarkers ",
                  "Argcis request results for ", {
                  type, result
                });
                createMarkers(result);
              },
              (err) => {
                // setErrors(err)
                log.error(
                  "[ccw-app],ccw-gadget,leaflet-map getMarkers",
                  "Argcis request results retunned error for ", {
                  type, err
                });
              }
            )
        }
        fetchData();
      }

      // generate the marker and point to map
      const createMarkers = (datamarkers: any) => {
        let markmap: any = [];
        let markersData = datamarkers.candidates;
        let markerBounds: any = []

        if (markersData && markersData.length > 0) {
          markersData.map(function (data: any) { //data.location.x
            let marker = L.marker([data.location.y, data.location.x], { icon: icon }).addTo(leafletMap.current.leafletElement);
            let addr = data.address + "," + data.attributes.Place_Addr
            markerBounds.push([data.location.y, data.location.x])
            marker.bindPopup(addr);
            markmap.push(marker);
          });
          if (type === "pharmacy") {
            setPharAddr(markmap)
          }
          if (type === "hospital") {
            setHospAddr(markmap);
          }
          const map = leafletMap.current.leafletElement;
          map.fitBounds(markerBounds);
        } else {
          log.debug(
            "[ccw-app],ccw-gadget,leaflet-map createMakers",
            "Argcis response does nto have valid candidates cannot plot markers ", {
            datamarkers
          });
        }
      }
    }

  }

  /**
   * Pharmacy filter inovked on checkbox. used to collect nearest patient address
   *  markers for Pharmacies
   */
  const pharmacyFilter = (updateMapSettings: boolean = true) => {
    if (updateMapSettings) setMapSettings({...settings, pharmCheck: !pharmCheck})
    if (!pharmCheck) {
      getMarkers(pharmacyarcgis, phmyMarker, "pharmacy");
    } else {
      if (pharaddr != []) {
        removeMarkers(pharaddr);
      }
    }

  }

  const getPreferredPharmacyMapAddress = async () => {
    try {
      const results = await Promise.all(
        pharmacies?.map(
          ({ pharmacyaddress }) =>
            fetch(addressargcis + pharmacyaddress + '&f=json').then((response) => response.json())
        )
      );
      setPrefPharMapAddr(results);
    } catch (error) {
      log.error('[ccw-app],ccw-gadget,leaflet-map', ' error while retrivng geocode for preferred pharmacy address ', {
        error,
      });
      setPrefPharMapAddr([]);
    };
  };

  /**
   * Preferred pharmacies filter inovked on checkbox.
   */
  const preferredPharmacyFilter = (updateMapSettings: boolean = true) => {
    if (updateMapSettings) setMapSettings({...settings, prefPharCheck: !prefPharCheck})
    if (!prefPharCheck) {
      const map = leafletMap.current.leafletElement;
      const markers: any = [];
      prefPharMapAddr.forEach((result, id) => {
        const candidates = result?.candidates;
        if (candidates && candidates[0]?.location) {
          const mapCoordinates = candidates[0].location;
          // long,latitude returned
          let { x, y } = mapCoordinates;
          // center = L.LatLngExpression = [y, x];
          setCenter([y, x]);
          const theMarker = L.marker([y, x], { icon: phmyMarker }).addTo(map);

          let markerPopup = pharmacies[id].pharmacyaddress;
          theMarker.bindPopup(`
            <div class="preferred-pharmacy--label">PREFERRED</div>
            ${markerPopup}
          `).openPopup();
          markers.push(theMarker);
        } else {
          log.debug('[ccw-app],ccw-gadget,leaflet-map', 'geocode for pharmacy address requested', {
            pharmacyaddress: pharmacies[id].pharmacyaddress,
          });
          log.debug('[ccw-app],ccw-gadget,leaflet-map', 'geocode for pharmacy address results returned empty', {
            result,
          });
        }
      });

      setPrefPharAddr(markers);     
    } else {
      if (prefPharAddr != []) {
        removeMarkers(prefPharAddr);
      }
    }

  }

  /**
  * Hospital filter inovked on checkbox. used to collect nearest patient address
  *  markers for Hospitals and clinics
  */
  const hospitalFilter = (updateMapSettings: boolean = true) => {
    if (updateMapSettings) setMapSettings({...settings, hospCheck: !hospCheck})
    if (!hospCheck) {
      getMarkers(hospitalarcgis, hosMarker, "hospital");
    } else {
      if (hospaddr != []) {
        removeMarkers(hospaddr);
      }
    }
  }
  /**
   * This method will remove the existing markers that are showing in the map
   * @param markerArray
   */
  const removeMarkers = (markerArray: any) => {
    if (markerArray.length > 0) {
      const map = leafletMap.current.leafletElement;
      markerArray.map(function (marker: any) { //data.location.x
        map.removeLayer(marker);
      });
    }
  }

  /**
   * Calling Destination checkbox change handler
   */
  const handleCallDestCheckChange = (updateMapSettings: boolean = true) => {
    if (updateMapSettings) setMapSettings({...settings, callDestCheck: !callDestCheck})
    if (!callDestCheck) {
      setMapAddress(calldest?.calldest?.calledaddress);
    } else {
      setMapAddress(address ? address : '');
    }
  };

  return (
    <div ref={mainRef} className={classNames('map-container', (expanded && 'fullscreen') || '')}>
      <Row type="flex" gutter={[16, 16]}>
        <Col sm={expanded ? (contact_center_type && contact_center_type === 'finesse' ? null : 20) : (contact_center_type && contact_center_type === 'finesse' ? null : 16)} 
             xs={contact_center_type && contact_center_type === 'finesse' ? null : 24}
             span={expanded ? (contact_center_type && contact_center_type === 'finesse' ? 20 : null) : (contact_center_type && contact_center_type === 'finesse' ? 16 : null)}>
          <Map
            center={center}
            zoom={15}
            ref={leafletMap}
          >
            <TileLayer
              attribution={attribution}
              url={attributionurl}
            />
            <div className="pointer" />
          </Map>
        </Col>
        <Col sm={expanded ? (contact_center_type && contact_center_type === 'finesse' ? null : 4) : (contact_center_type && contact_center_type === 'finesse' ? null : 8)} 
             xs={contact_center_type && contact_center_type === 'finesse' ? null : 24}
             span={expanded ? (contact_center_type && contact_center_type === 'finesse' ? 4 : null) : (contact_center_type && contact_center_type === 'finesse' ? 8 : null)}>
          <Row type="flex" gutter={[8, 8]}>
            <Col sm={contact_center_type && contact_center_type === 'finesse' ? null : 24} 
                 xs={contact_center_type && contact_center_type === 'finesse' ? null : 12}
                 span={contact_center_type && contact_center_type === 'finesse' ? 24 : null}>
              <div className="box__map-filter-title">Filter:</div>
            </Col>
            <Col sm={contact_center_type && contact_center_type === 'finesse' ? null : 24} 
                 xs={contact_center_type && contact_center_type === 'finesse' ? null : 12}
                 span={contact_center_type && contact_center_type === 'finesse' ? 24 : null}>
              <Checkbox className="ant-checkbox--small" checked={pharmCheck}
                onChange={
                  pharmacyFilter
                } >Pharmacies</Checkbox>
            </Col>
            <Col sm={contact_center_type && contact_center_type === 'finesse' ? null : 24} 
                 xs={contact_center_type && contact_center_type === 'finesse' ? null : 12}
                 span={contact_center_type && contact_center_type === 'finesse' ? 24 : null}>
              <Checkbox className="ant-checkbox--small" checked={prefPharCheck}
                onChange={
                  preferredPharmacyFilter
                } >Preferred Pharmacy</Checkbox>
            </Col>
            <Col sm={contact_center_type && contact_center_type === 'finesse' ? null : 24} 
                 xs={contact_center_type && contact_center_type === 'finesse' ? null : 12}
                 span={contact_center_type && contact_center_type === 'finesse' ? 24 : null}>
              <Checkbox className="ant-checkbox--small" checked={hospCheck}
                onChange={
                  hospitalFilter
                }>Clinics & Hospitals</Checkbox>
            </Col>
            {calldest && (
              <Col sm={contact_center_type && contact_center_type === 'finesse' ? null : 24} 
                   xs={contact_center_type && contact_center_type === 'finesse' ? null : 12}
                   span={contact_center_type && contact_center_type === 'finesse' ? 24 : null}>
                <Checkbox className="ant-checkbox--small" checked={callDestCheck} onChange={handleCallDestCheckChange}>
                  Calling Destination
                </Checkbox>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>

  );
})
